import { Check, Minus } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useScrollAnim } from '../../hooks/use-scroll-anim'
import type { Plan, FeatureKey } from '../../types'

type CellValue = boolean | string

interface ComparisonFeature {
  key: string
  labelZh: string
  labelEn: string
}

const featureKeys: ComparisonFeature[] = [
  { key: 'chat', labelZh: '基础AI对话', labelEn: 'Basic AI Chat' },
  { key: 'rag', labelZh: '知识库问答', labelEn: 'Knowledge Base' },
  { key: 'multimodal', labelZh: '多模态对话', labelEn: 'Multimodal Chat' },
  { key: 'deepseek', labelZh: 'DeepSeek推理', labelEn: 'DeepSeek Reasoning' },
  { key: 'priority', labelZh: '优先技术支持', labelEn: 'Priority Support' },
]

function formatTokenQuota(n: number, lang: string) {
  if (n >= 10000000) return lang === 'zh' ? `${(n / 10000000).toFixed(0)}千万` : `${(n / 1000000).toFixed(0)}M`
  if (n >= 10000) return lang === 'zh' ? `${(n / 10000).toFixed(0)}万` : `${(n / 1000).toFixed(0)}K`
  return n.toLocaleString()
}

function parseFeatures(featuresStr: string): FeatureKey[] {
  try {
    return JSON.parse(featuresStr)
  } catch {
    return []
  }
}

function CellValueRenderer({ value }: { value: CellValue }) {
  if (typeof value === 'boolean') {
    return value
      ? <Check className="h-4 w-4 text-emerald-500 mx-auto" />
      : <Minus className="h-4 w-4 text-gray-200 mx-auto" />
  }
  return <span className="text-xs text-gray-600">{value}</span>
}

interface PricingComparisonTableProps {
  plans: Plan[]
}

export function PricingComparisonTable({ plans }: PricingComparisonTableProps) {
  const { lang, t } = useI18n()
  const { ref, isVisible } = useScrollAnim()

  // 过滤掉加购包，只显示主套餐
  const mainPlans = plans
    .filter((p) => !p.planCode.startsWith('TOPUP'))
    .sort((a, b) => a.sortOrder - b.sortOrder)

  if (mainPlans.length === 0) return null

  const popularPlanCode = 'PRO'

  return (
    <section ref={ref} className="py-8">
      <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <h2 className="text-2xl font-bold text-[#1a1a1a] text-center mb-8">
          {t('功能对比', 'Feature Comparison')}
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/[0.06]">
                <th className="text-left py-3 px-4 text-gray-400 font-medium w-40">{t('功能', 'Feature')}</th>
                {mainPlans.map((plan) => (
                  <th
                    key={plan.id}
                    className={`py-3 px-4 text-center font-medium ${plan.planCode === popularPlanCode ? 'text-orange-600' : 'text-[#1a1a1a]'}`}
                  >
                    {plan.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Token额度行 */}
              <tr className="border-b border-black/[0.03] hover:bg-orange-50/20 transition-colors">
                <td className="py-3 px-4 text-gray-500">{t('Token 额度/月', 'Tokens/mo')}</td>
                {mainPlans.map((plan) => (
                  <td key={plan.id} className="py-3 px-4 text-center">
                    <span className="text-xs text-gray-600">{formatTokenQuota(plan.tokenQuota, lang)}</span>
                  </td>
                ))}
              </tr>
              {/* 最大用户数行 */}
              <tr className="border-b border-black/[0.03] hover:bg-orange-50/20 transition-colors">
                <td className="py-3 px-4 text-gray-500">{t('最大用户数', 'Max Users')}</td>
                {mainPlans.map((plan) => (
                  <td key={plan.id} className="py-3 px-4 text-center">
                    <span className="text-xs text-gray-600">
                      {plan.maxUsers > 0 ? plan.maxUsers : t('不限', 'Unlimited')}
                    </span>
                  </td>
                ))}
              </tr>
              {/* 功能行 */}
              {featureKeys.map((feat) => (
                <tr key={feat.key} className="border-b border-black/[0.03] hover:bg-orange-50/20 transition-colors">
                  <td className="py-3 px-4 text-gray-500">{t(feat.labelZh, feat.labelEn)}</td>
                  {mainPlans.map((plan) => {
                    const planFeatures = parseFeatures(plan.features)
                    return (
                      <td key={plan.id} className="py-3 px-4 text-center">
                        <CellValueRenderer value={planFeatures.includes(feat.key as FeatureKey)} />
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}
